/**
 * CacheStatsWidget — live hit-rate readout for the in-process response
 * cache. Polls /api/admin/cache-stats every 10s. Shows hits vs misses,
 * current entry count, hit-rate %, and a tiny sparkline-style bar so
 * the "cache warming up" moment is visible.
 *
 * Kept small on purpose — this is a diagnostic strip on the admin
 * dashboard, not a full observability dashboard. Falls back to a
 * neutral "checking…" state on 401/403 so it never blocks page render
 * for non-admins.
 */
import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Gauge, Loader2 } from "lucide-react";

export default function CacheStatsWidget() {
  const [stats, setStats] = useState(null);
  const [checking, setChecking] = useState(true);
  const [gated, setGated] = useState(false);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const { data } = await api.get("/admin/cache-stats");
        if (cancelled) return;
        setStats(data);
        setChecking(false);
      } catch (err) {
        if (cancelled) return;
        const status = err?.response?.status;
        // 401/403 — user isn't admin. Silently hide the widget rather
        // than crashing the dashboard for a plausibly-authed viewer.
        if (status === 401 || status === 403) setGated(true);
        setChecking(false);
      }
    };
    load();
    const id = setInterval(load, 10000);   // 10s poll
    return () => { cancelled = true; clearInterval(id); };
  }, []);

  if (gated) return null;
  if (checking && !stats) {
    return (
      <div className="rounded-lg border border-stone-200 bg-white p-4 flex items-center gap-2 text-stone-500 text-sm">
        <Loader2 className="h-4 w-4 animate-spin" /> Checking cache…
      </div>
    );
  }
  if (!stats) return null;

  const {
    size = 0, max = 0, hits = 0, misses = 0,
    sets = 0, evictions = 0, hit_rate_pct = 0,
  } = stats;
  const total = hits + misses;
  const barWidth = Math.min(100, Math.max(0, hit_rate_pct));
  // Color hint: green ≥60%, amber 30-60%, red <30%. Empty cache = neutral.
  const barColor = total === 0
    ? "bg-stone-300"
    : hit_rate_pct >= 60
      ? "bg-emerald-500"
      : hit_rate_pct >= 30
        ? "bg-amber-500"
        : "bg-red-500";

  return (
    <div
      data-testid="admin-cache-stats-widget"
      className="rounded-lg border border-stone-200 bg-white p-4"
    >
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-2">
          <Gauge className="h-4 w-4 text-[#4C1D95]" />
          <div className="text-[11px] font-underground-sc tracking-wider text-[#4C1D95] font-semibold">
            Response cache · per-worker
          </div>
        </div>
        <div className="text-[11px] font-mono text-stone-400">refreshes every 10s</div>
      </div>

      <div className="mt-3 grid grid-cols-2 sm:grid-cols-5 gap-3">
        <Stat label="Hit rate" value={`${hit_rate_pct}%`} testid="cache-stat-hit-rate" bold />
        <Stat label="Hits" value={hits.toLocaleString()} testid="cache-stat-hits" />
        <Stat label="Misses" value={misses.toLocaleString()} testid="cache-stat-misses" />
        <Stat label="Entries" value={`${size} / ${max}`} testid="cache-stat-size" />
        <Stat label="Evictions" value={evictions.toLocaleString()} testid="cache-stat-evictions" />
      </div>

      <div className="mt-3 h-1.5 w-full bg-stone-100 rounded-full overflow-hidden">
        <div
          data-testid="cache-stat-bar"
          className={`h-full ${barColor} transition-[width] duration-500`}
          style={{ width: `${barWidth}%` }}
        />
      </div>
      <div className="mt-2 text-[11px] text-stone-500">
        {total === 0
          ? "Cache is empty. Numbers will populate as Quick Check / Advanced Search traffic arrives."
          : `${total.toLocaleString()} lookup${total === 1 ? "" : "s"} · ${sets.toLocaleString()} write${sets === 1 ? "" : "s"} · ${hit_rate_pct >= 60 ? "cache is earning its keep." : hit_rate_pct >= 30 ? "warming up." : "still cold — needs more traffic."}`}
      </div>
    </div>
  );
}

function Stat({ label, value, testid, bold }) {
  return (
    <div data-testid={testid}>
      <div className="text-[10px] font-underground-sc tracking-wider text-stone-500">{label}</div>
      <div className={`mt-0.5 font-mono ${bold ? "text-lg font-semibold text-stone-900" : "text-sm text-stone-700"}`}>
        {value}
      </div>
    </div>
  );
}
